// src/services/BusinessVerificationService.js - ReactJS (web)

import api, { formDataApi } from '../api/api';

class BusinessVerificationService {
  // ==================== PUBLIC ROUTES ====================
  // No authentication required

  /**
   * Get verification status for a provider
   * GET /service-providers/:providerId/verification/status
   */
  async getVerificationStatus(providerId) {
    try {
      const response = await api.get(
        `/service-providers/${providerId}/verification/status`,
        { public: true } // ✅ Public route
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get document types
   * GET /service-providers/verification/document-types
   */
  async getDocumentTypes() {
    try {
      const response = await api.get(
        `/service-providers/verification/document-types`,
        { public: true } // ✅ Public route
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get verification statuses
   * GET /service-providers/verification/statuses
   */
  async getVerificationStatuses() {
    try {
      const response = await api.get(
        `/service-providers/verification/statuses`,
        { public: true } // ✅ Public route
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PROTECTED ROUTES ====================
  // Routes that require authentication

  /**
   * Submit provider for verification
   * POST /service-providers/:providerId/verification/submit
   */
  async submitForVerification(providerId, documents) {
    try {
      const response = await api.post(
        `/service-providers/${providerId}/verification/submit`,
        { documents }
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get verification requirements
   * GET /service-providers/:providerId/verification/requirements
   */
  async getVerificationRequirements(providerId) {
    try {
      const response = await api.get(
        `/service-providers/${providerId}/verification/requirements`
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update verification documents
   * PUT /service-providers/:providerId/verification/documents
   */
  async updateVerificationDocuments(providerId, documents) {
    try {
      const response = await api.put(
        `/service-providers/${providerId}/verification/documents`,
        { documents }
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove a verification document
   * DELETE /service-providers/:providerId/verification/documents/:documentId
   */
  async removeVerificationDocument(providerId, documentId) {
    try {
      const response = await api.delete(
        `/service-providers/${providerId}/verification/documents/${documentId}`
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get verification history for a provider
   * GET /service-providers/:providerId/verification/history
   */
  async getVerificationHistory(providerId) {
    try {
      const response = await api.get(
        `/service-providers/${providerId}/verification/history`
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Resend verification email
   * POST /service-providers/:providerId/verification/resend-email
   */
  async resendVerificationEmail(providerId) {
    try {
      const response = await api.post(
        `/service-providers/${providerId}/verification/resend-email`,
        {}
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload verification document
   * POST /service-providers/:providerId/verification/upload-document
   */
  async uploadVerificationDocument(providerId, formData) {
    try {
      const response = await formDataApi.post(
        `/service-providers/${providerId}/verification/upload-document`,
        formData,
        {
          timeout: 60000, // 60 seconds for large files
          // ✅ No public flag - token will be attached
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ADMIN ROUTES ====================
  // Routes that require admin authentication

  /**
   * Get pending verifications (Admin only)
   * GET /service-providers/verification/pending
   */
  async getPendingVerifications(page = 1, limit = 20) {
    try {
      const response = await api.get(
        `/service-providers/verification/pending`,
        { params: { page, limit } }
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get verification statistics (Admin only)
   * GET /service-providers/verification/statistics
   */
  async getVerificationStatistics() {
    try {
      const response = await api.get(
        `/service-providers/verification/statistics`
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Verify a provider (Admin only)
   * POST /service-providers/:providerId/verification/verify
   */
  async verifyProvider(providerId, adminNotes = null) {
    try {
      const response = await api.post(
        `/service-providers/${providerId}/verification/verify`,
        { adminNotes }
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reject a provider's verification (Admin only)
   * POST /service-providers/:providerId/verification/reject
   */
  async rejectProvider(providerId, reason) {
    try {
      const response = await api.post(
        `/service-providers/${providerId}/verification/reject`,
        { reason }
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Suspend a verified provider (Admin only)
   * POST /service-providers/:providerId/verification/suspend
   */
  async suspendProvider(providerId, reason) {
    try {
      const response = await api.post(
        `/service-providers/${providerId}/verification/suspend`,
        { reason }
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reinstate a suspended provider (Admin only)
   * POST /service-providers/:providerId/verification/reinstate
   */
  async reinstateProvider(providerId) {
    try {
      const response = await api.post(
        `/service-providers/${providerId}/verification/reinstate`,
        {}
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Bulk verify providers (Admin only)
   * POST /service-providers/verification/bulk/verify
   */
  async bulkVerifyProviders(providerIds) {
    try {
      const response = await api.post(
        `/service-providers/verification/bulk/verify`,
        { providerIds }
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Bulk reject providers (Admin only)
   * POST /service-providers/verification/bulk/reject
   */
  async bulkRejectProviders(providerIds, reason) {
    try {
      const response = await api.post(
        `/service-providers/verification/bulk/reject`,
        { providerIds, reason }
        // ✅ No public flag - token will be attached
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      const message = error.response.data?.message || error.response.data?.error || 'An error occurred';
      const status = error.response.status;
      return new Error(`${message} (${status})`);
    } else if (error.request) {
      return new Error('No response from server. Please check your internet connection.');
    } else {
      return new Error(error.message || 'An unexpected error occurred');
    }
  }
}

export default new BusinessVerificationService();